// Display · entitySelectRendererContext — renderer contract, context, hook.
//
// Split out of `EntitySelectContext.tsx` so that module exports components only.

import { createContext, useContext, type ReactNode, type CSSProperties } from "react";

// ─── Renderer props (minimal contract for entity-select consumers) ──────────

export interface EntitySelectRendererProps {
  /** Entity kind string (matches EntityKind in connected/EntitySelect). */
  kind: string;
  /** Optional role filter (for employee kind). */
  role?: string;
  /** AntD Select size. Default `"middle"`. */
  size?: "small" | "middle" | "large";
  /** Inline style applied to the Select root. */
  style?: CSSProperties;
  /** Currently selected value. */
  value?: string | number | null;
  /** Auto-focus on mount. */
  autoFocus?: boolean;
  /** Called when the selection changes. */
  onChange?: (value: string | number | null) => void;
  /** Called when the select loses focus. */
  onBlur?: () => void;
  /** `data-testid` forwarded to the Select root. */
  testId?: string;
  /**
   * Additional props forwarded verbatim to the underlying EntitySelect
   * (e.g. `allowClear`, `mode`, `placeholder`, `context`, `category`).
   * These are entity-select-specific and not part of the minimal context
   * contract — callers that need them pass via this escape hatch.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// ─── Context ─────────────────────────────────────────────────────────────────

export type EntitySelectRenderer = (props: EntitySelectRendererProps) => ReactNode;

/** No-op fallback — renders nothing when no provider is mounted. */
function defaultRenderer(_props: EntitySelectRendererProps): ReactNode {
  return null;
}

export const EntitySelectContext = createContext<EntitySelectRenderer>(defaultRenderer);

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * Returns the current entity-select renderer.
 *
 * ```tsx
 * const renderSelect = useEntitySelectRenderer();
 * return renderSelect({ kind: "employee", role: "logistics", value: id, onChange });
 * ```
 */
export function useEntitySelectRenderer(): EntitySelectRenderer {
  return useContext(EntitySelectContext);
}
